import {
  lineSegments,
  lowerBound,
  segmentDistances,
  smoothSeries,
  trackTimeSegments,
} from '@shared/geoutils.js';
import type { Feature, LineString, MultiLineString } from 'geojson';
import type { ReactNode } from 'react';
import type { Messages } from '@/translations/messagesInterface.js';
import type { ColorizerMessages } from './translations/ColorizerMessages.js';

type ScaleTick = { t: number; label: ReactNode; inside?: boolean };

/** How a mode's palette positions read as values, for the legend and readouts. */
export type ColorizeScale = {
  /** Shown once next to the legend label (e.g. `%`), not repeated on each tick. */
  unit?: string;
  ticks: ScaleTick[];
  /** The value at a palette position, 0 to 1, as text. */
  valueAt?: (t: number) => string;
  /** Has more ticks than the legend's default width holds. */
  wide?: boolean;
};

/** What a fixed scale labels itself with. */
export type ScaleContext = {
  cm: ColorizerMessages;
  cardinals: Messages['cardinals'];
  language: string;
  steepnessScale: number;
};

/** A number with a fixed count of decimals, and its unit if given. */
export function formatScaleNumber(
  value: number,
  digits: number,
  language: string,
  unit?: string,
  options?: Intl.NumberFormatOptions,
): string {
  return `${value.toLocaleString(language, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    ...options,
  })}${unit ? ` ${unit}` : ''}`;
}

/**
 * A straight scale from `min` to `max`, ticked at the given palette positions;
 * numbered with `digits`, or labelled by `format`.
 */
export function linearScale(
  { min, max }: { min: number; max: number },
  language: string,
  ticks: number[],
  {
    digits = 0,
    unit,
    format,
    wide,
  }: {
    digits?: number;
    unit?: string;
    format?: (value: number) => string;
    wide?: boolean;
  } = {},
): ColorizeScale {
  const at = (t: number) => min + t * (max - min);

  const label = (t: number) =>
    format ? format(at(t)) : formatScaleNumber(at(t), digits, language);

  return {
    unit,
    wide,
    valueAt: (t) =>
      format ? label(t) : formatScaleNumber(at(t), digits, language, unit),
    ticks: ticks.map((t) => ({ t, label: label(t) })),
  };
}

/** Min/max of finite numbers, or null if fewer than two distinct values. */
export function span(
  values: Iterable<number>,
): { min: number; max: number } | null {
  let min = Infinity;

  let max = -Infinity;

  for (const v of values) {
    if (Number.isFinite(v)) {
      if (v < min) {
        min = v;
      }

      if (v > max) {
        max = v;
      }
    }
  }

  return max > min ? { min, max } : null;
}

export type HotlinePalette = Array<{
  r: number;
  g: number;
  b: number;
  t: number;
}>;

/**
 * The Hotline options a colorizer imposes, taken from the same place as the
 * palette so a new caller cannot forget them: a span-based mode changes color
 * across coincident points, which Leaflet's simplification would drop.
 */
export function colorizerHotlineOptions(
  colorizer: Colorizer | null | undefined,
): { palette: HotlinePalette | undefined; smoothFactor?: number } {
  return {
    palette: colorizer?.palette,
    ...(colorizer?.spanBased && { smoothFactor: 0 }),
  };
}

/**
 * The geometry a colorizer should read: a span-based mode takes the router's own
 * values off the plain line, where every other mode wants the densified one the
 * elevation pipeline built.
 */
export function colorizeGeometrySource<T>(
  colorizer: Colorizer | null | undefined,
  densified: T | null | undefined,
): T | null {
  return !colorizer || colorizer.spanBased ? null : (densified ?? null);
}

/** A palette color as CSS, the one spelling both the line and the legend use. */
export function rgbCss(color: [r: number, g: number, b: number]): string {
  return `rgb(${color.join(' ')})`;
}

/**
 * The colorizer to paint with: the picked one, unless it has nothing to say
 * about these features, in which case nothing is painted and whatever the lines
 * carry themselves stands.
 */
export function availableColorizer(
  picked: Colorizer | null | undefined,
  features: Feature<LineString>[],
): Colorizer | null {
  return picked && (!picked.isAvailable || picked.isAvailable(features))
    ? picked
    : null;
}

/**
 * The palette read at `t` (a {@link ColorizedPoint}'s `color`), interpolated
 * between the stops it falls between — what Hotline paints on the map, for a
 * reader that has to spell the color itself. Stops are in ascending `t`.
 */
export function paletteColorAt(
  palette: HotlinePalette,
  t: number,
): [r: number, g: number, b: number] {
  const first = palette[0];

  if (!first) {
    return [0, 0, 0];
  }

  let prev = first;

  for (const stop of palette) {
    if (t <= stop.t) {
      const span = stop.t - prev.t;

      const k = span > 0 ? (t - prev.t) / span : 0;

      return [
        Math.round(prev.r + (stop.r - prev.r) * k),
        Math.round(prev.g + (stop.g - prev.g) * k),
        Math.round(prev.b + (stop.b - prev.b) * k),
      ];
    }

    prev = stop;
  }

  return [prev.r, prev.g, prev.b];
}

/**
 * A stretch of a route the router reported one value for, in metres along the
 * line. Metres rather than point indices because the line colorize draws is
 * densified for premium users, which shifts every index.
 */
export type PathDetailSpan = { start: number; end: number; value: string };

export type PathDetails = Record<string, PathDetailSpan[]>;

/**
 * Where {@link PathDetailSpan}s ride on the feature being colorized: one
 * {@link PathDetails} per part of the line, so a multi-segment track keeps its
 * spans to itself without being cut into features to carry them.
 */
export const PATH_DETAILS_PROP = 'fm:pathDetails';

/** The spans a single line is painted with. */
export function readPathDetails(
  feature: Feature<LineString>,
  key: string,
): PathDetailSpan[] | undefined {
  return readPathDetailsParts(feature)?.[0]?.[key];
}

/**
 * Every part's spans. Saved tracks may hold one object measured along the
 * whole line, which is cut into parts here.
 */
export function readPathDetailsParts(
  feature: Feature,
): PathDetails[] | undefined {
  const details = feature.properties?.[PATH_DETAILS_PROP] as
    | PathDetails[]
    | PathDetails
    | undefined;

  if (!details || Array.isArray(details)) {
    return details;
  }

  const { geometry } = feature;

  return geometry?.type === 'MultiLineString'
    ? perPartPathDetails(details, geometry)
    : [details];
}

/**
 * `details` as one entry per part of `geometry`, each clipped to its own part
 * and measured from its start. The axis skips the gap between two parts, as
 * the distance a colorizer walks over the parts does.
 */
function perPartPathDetails(
  details: PathDetails,
  geometry: LineString | MultiLineString,
): PathDetails[] {
  return segmentDistances(lineSegments(geometry)).map((distances) =>
    clipPathDetails(details, distances[0] ?? 0, distances.at(-1) ?? 0),
  );
}

/** The spans covering `[from, to]`, measured from `from`. */
export function clipPathDetails(
  details: PathDetails,
  from: number,
  to: number,
): PathDetails {
  return Object.fromEntries(
    Object.entries(details).flatMap(([key, spans]) => {
      const clipped: PathDetailSpan[] = [];

      for (const span of spans) {
        const start = Math.max(span.start, from);

        const end = Math.min(span.end, to);

        if (end > start) {
          clipped.push({
            start: start - from,
            end: end - from,
            value: span.value,
          });
        }
      }

      return clipped.length > 0 ? [[key, clipped] as const] : [];
    }),
  );
}

/**
 * The same spans read off another measure of the same vertices: `from` and `to`
 * hold one distance per vertex, each along its own axis, and a span between two
 * vertices is interpolated. Simplifying a line and joining two both move every
 * vertex along the axis the spans are metres of.
 *
 * A gap an axis leaves out is two vertices at the same distance, so a span
 * starting there takes the later of them and one ending there the earlier —
 * both taking the same one would stretch the span across the gap.
 */
export function remapPathDetails(
  details: PathDetails,
  from: number[],
  to: number[],
): PathDetails {
  const last = from.length - 1;

  const at = (meters: number, starting: boolean) => {
    if (last < 0) {
      return meters;
    }

    const i = lowerBound(from.length, (index) =>
      starting ? from[index]! > meters : from[index]! >= meters,
    );

    if (i <= 0) {
      return to[0]!;
    }

    if (i > last) {
      return to[last]!;
    }

    const width = from[i]! - from[i - 1]!;

    const f = width > 0 ? (meters - from[i - 1]!) / width : 0;

    return to[i - 1]! + f * (to[i]! - to[i - 1]!);
  };

  return Object.fromEntries(
    Object.entries(details).map(([key, spans]) => [
      key,
      spans.map((span) => ({
        ...span,
        start: at(span.start, true),
        end: at(span.end, false),
      })),
    ]),
  );
}

/** One row of a legend that names categories instead of drawing a scale. */
export type CategoryShare = {
  key: string;
  label: string;
  color: string;
  /** How far it runs, so the legend doubles as what the route is made of. */
  meters: number;
};

export interface ColorizedPoint {
  lat: number;
  lon: number;
  color: number;
  // True when the underlying value is missing. The colorized line breaks at
  // such points instead of drawing through a misleading mid-palette color.
  gap?: boolean;
}

// {@link noDataRuns} stretches a colorizer can't value are drawn as a neutral,
// slightly thinner, semi-transparent line: it reads as "no value here" and
// stays distinct from a full-strength colored run by form rather than hue, so
// it never collides with a palette color (e.g. the gray of `elevation`).
export const NO_DATA_COLOR = '#808080';
export const NO_DATA_OPACITY = 2 / 3;

/**
 * Split colorized points into contiguous runs, breaking at gaps (points whose
 * value is missing). Runs shorter than two points can't form a line and are
 * dropped, leaving a hole the {@link noDataRuns} fill spans in a neutral color.
 */
export function splitOnGaps(points: ColorizedPoint[]): ColorizedPoint[][] {
  const runs: ColorizedPoint[][] = [];

  let current: ColorizedPoint[] = [];

  for (const point of points) {
    if (point.gap) {
      if (current.length > 1) {
        runs.push(current);
      }

      current = [];
    } else {
      current.push(point);
    }
  }

  if (current.length > 1) {
    runs.push(current);
  }

  return runs;
}

/**
 * The complement of {@link splitOnGaps}: runs covering every edge that touches
 * a gap, including the valid points bordering each gap so the run connects to
 * the colorized line. Drawn in {@link NO_DATA_COLOR} so stretches the colorizer
 * has no value for stay visible instead of leaving a hole.
 */
export function noDataRuns(points: ColorizedPoint[]): ColorizedPoint[][] {
  const runs: ColorizedPoint[][] = [];

  let current: ColorizedPoint[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    if (points[i]!.gap || points[i + 1]!.gap) {
      if (current.length === 0) {
        current.push(points[i]!);
      }

      current.push(points[i + 1]!);
    } else if (current.length > 0) {
      runs.push(current);

      current = [];
    }
  }

  if (current.length > 0) {
    runs.push(current);
  }

  return runs;
}

export interface ColorizeOptions {
  // Current integer map zoom. Value-smoothing windows widen when zoomed out so
  // sub-pixel wiggle in the source data doesn't read as color noise; omitted
  // (e.g. export, tests) the colorizer keeps its intrinsic baseline span.
  zoom?: number;
  // The same thing said directly, for a reader that knows its own scale rather
  // than a map zoom: the elevation chart's x axis is metres of line per pixel.
  // Takes precedence over `zoom`.
  metersPerPixel?: number;
  // The grade the steepness palette ends at, as a ratio; omitted, its default.
  steepnessScale?: number;
}

export interface Colorizer {
  palette: HotlinePalette;
  isAvailable?: (features: Feature<LineString>[]) => boolean;
  compute: (
    features: Feature<LineString>[],
    options?: ColorizeOptions,
  ) => ColorizedPoint[][];
  // Derived from the elevation coordinate, so it benefits from the same
  // fill/override prompt the elevation chart uses.
  needsElevation?: boolean;
  // The legend of a per-feature-normalized scalar mode: its unit, and the
  // smoothed series it colors by, so the labels match the line, not raw samples.
  legend?: {
    unit?: string;
    // Its values are smoothed by zoom, so a zoom change rescans them.
    smoothed?: true;
    // Its labels are wide, so the bar is too.
    wide?: true;
    // Decimals to label it with; 0 when omitted.
    digits?: number;
    // Labels values other than as numbers, e.g. timestamps as times of day; made
    // for the line's range, as a time needs its date only on a long one.
    format?: (
      language: string,
      range: { min: number; max: number },
    ) => (value: number) => string;
    values: (
      feature: Feature<LineString>,
      options?: ColorizeOptions,
    ) => number[];
  };
  // The legend for a mode that names categories instead of drawing a scale: the
  // ones the lines actually hold, with how far each runs. The colorizer answers
  // it, so the legend never has to know how a mode stores its values.
  categories?: (
    features: Feature<LineString>[],
    messages: ColorizerMessages,
  ) => CategoryShare[];
  // The name of the category painted at a palette position, for a readout.
  categoryAt?: (color: number, messages: ColorizerMessages) => string;
  // The legend of a scale that means the same on every line.
  scale?: (context: ScaleContext) => ColorizeScale;
  // Set by a mode that paints stretches the router reported rather than a value
  // per point. Such a line changes color across two coincident points, so
  // Leaflet's vertex simplification has to be off for it (`smoothFactor`); it
  // reads nothing from the elevation-densified line, and its result is the same
  // at every zoom.
  spanBased?: true;
  // The path detail this mode reads, as GraphHopper names it. A menu compares
  // it against what the active profile asks for, so a mode the router will
  // never report for that profile is left out rather than shown dead. Several,
  // where the profile decides which one the mode is answered with — the mode
  // then reads whichever the route carries.
  detail?: string | string[];
}

/**
 * The smoothed per-point series {@link colorizeByValues} normalizes against. The
 * legend reads these (not the raw samples) so its labels match the colored line.
 */
export function smoothedValues(spec: {
  coords: number[][];
  values: number[];
  smoothSpan?: number;
}): number[] {
  return smoothSeries(spec.coords, spec.values, spec.smoothSpan ?? 0);
}

/**
 * Build positions by normalizing per-coord values to 0..1 via min/max across
 * each feature. A `smoothSpan` (metres) low-passes the values along the path
 * before normalizing — the shared zoom-aware generalization every scalar mode
 * uses. NaN inputs are flagged as gaps from the *original* samples (the line
 * breaks there); they keep the mid-palette color (0.5) only as a harmless
 * filler, which is also the fallback when the value range is degenerate.
 * Keeping the Hotline input within [0, 1] avoids CanvasGradient crashes.
 */
export function colorizeByValues(
  features: Feature<LineString>[],
  perFeature: (feature: Feature<LineString>) => {
    coords: number[][];
    values: number[];
    smoothSpan?: number;
    // Fixed `[min, max]` for an absolute scale, with the color clamped into it
    // so it means the same across tracks; omitted, values normalize against the
    // feature's own smoothed min/max.
    range?: [number, number];
  },
): ColorizedPoint[][] {
  return features.map((feature) => {
    const {
      coords,
      values,
      smoothSpan,
      range: fixedRange,
    } = perFeature(feature);

    const smoothed = smoothSeries(coords, values, smoothSpan ?? 0);

    let min: number;

    let max: number;

    if (fixedRange) {
      [min, max] = fixedRange;
    } else {
      const valid = smoothed.filter((v): v is number => Number.isFinite(v));

      min = valid.length ? Math.min(...valid) : 0;

      max = valid.length ? Math.max(...valid) : 0;
    }

    const range = max - min;

    return coords.map((coord, i) => {
      const v = smoothed[i];

      // Gaps come from the original sample, not the smoothed value, which may
      // have carried a neighbor forward across a hole.
      const gap = !Number.isFinite(values[i]);

      const color =
        range > 0 && Number.isFinite(v)
          ? Math.max(0, Math.min(1, (v - min) / range))
          : 0.5;

      return { lat: coord[1], lon: coord[0], color, gap };
    });
  });
}

export function readNumericArray(
  feature: Feature<LineString>,
  key: string,
  expectedLength: number,
): number[] | null {
  const cp = feature.properties?.['coordinateProperties'] as
    | Record<string, unknown>
    | undefined;

  const raw = cp?.[key];

  if (!Array.isArray(raw) || raw.length !== expectedLength) {
    return null;
  }

  // togeojson fills the array with `null` and writes values where present;
  // map them to NaN so colorizeByValues filters them out of the range.
  return raw.map((v) => (typeof v === 'number' ? v : NaN));
}

export function hasNumericArray(
  features: Feature<LineString>[],
  key: string,
): boolean {
  return features.some((f) => {
    const arr = readNumericArray(f, key, f.geometry.coordinates.length);

    return arr?.some((v) => Number.isFinite(v));
  });
}

/**
 * Reads per-point timestamps as epoch millis. togeojson puts GPX/KML times under
 * `coordinateProperties.times`; live tracking writes a top-level `coordTimes`.
 * Returns null unless an array of the expected length is present; bad entries
 * become NaN.
 */
export function readCoordTimes(
  feature: Feature<LineString>,
  expectedLength: number,
): number[] | null {
  // A colorized line is a single `LineString`, so its times are one segment.
  const segments = trackTimeSegments(feature);

  const raw = segments.length === 1 ? segments[0]! : segments.flat();

  if (raw.length !== expectedLength) {
    return null;
  }

  return raw.map((t) => (typeof t === 'string' ? new Date(t).getTime() : NaN));
}
